import { onUnmounted } from 'vue';
import { useSessionStore } from '@/stores/session.store';
import { useDomainsStore } from '@/stores/domains.store';

/**
 * Keeps the domains store cache in sync with session store changes.
 *
 * The session store is the sole authority for auth/permissions. When roles or
 * org memberships change (detected via a 403 → reactive re-fetch), the domains
 * store's display cache may be stale. This composable subscribes to session
 * changes and invalidates the domains cache precisely when needed, so admin
 * pages always show accurate data after a role change.
 *
 * Call once from the root App component.
 */
export const useStoreCoordination = () => {
  const sessionStore = useSessionStore();
  const domainsStore = useDomainsStore();

  let prevIsAdmin = sessionStore.currentUser?.is_admin ?? false;
  let prevIsLensSupport = sessionStore.currentUser?.is_lens_support ?? false;
  let prevOrgCount = sessionStore.organisations?.length ?? 0;

  const unsubscribe = sessionStore.$subscribe(() => {
    const currIsAdmin = sessionStore.currentUser?.is_admin ?? false;
    const currIsLensSupport = sessionStore.currentUser?.is_lens_support ?? false;
    const currOrgCount = sessionStore.organisations?.length ?? 0;

    // Role revoked → invalidate admin domain tree cache so the next admin
    // page visit fetches fresh data and won't show stale organisation trees.
    if ((prevIsAdmin && !currIsAdmin) || (prevIsLensSupport && !currIsLensSupport)) {
      domainsStore.invalidateCache();
    }

    // Organisation removed → invalidate full cache so org-related display
    // data is refetched rather than served from a potentially stale snapshot.
    if (currOrgCount < prevOrgCount) {
      domainsStore.invalidateCache();
    }

    prevIsAdmin = currIsAdmin;
    prevIsLensSupport = currIsLensSupport;
    prevOrgCount = currOrgCount;
  });

  onUnmounted(() => unsubscribe());
};
